// HeaderLayout.jsx - Enhanced responsive component
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  useMediaQuery,
  Stack,
  Modal,
  Fade,
  Backdrop,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import CalendarModal from "../CalendarModal/CalendarModal";

// Enhanced ActionButton with better hover effects and sizing
const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 9999,
  padding: theme.spacing(1.5, 4),
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s ease-in-out",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.25)",
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(255, 255, 255, 0.1)",
    transform: "translateX(-100%)",
    transition: "transform 0.6s ease-in-out",
  },
  "&:hover::after": {
    transform: "translateX(0)",
  }
}));

const HeaderLayout = ({
  backgroundImage,
  title,
  subtitle,
  imageUrl,
  imageAlt = "Platform Demo",
  ctaButtonText = "Get Started",
  ctaButtonLink = "https://flow.beanbag.ai/signup",
  secondaryButtonText = "Request a Demo",
  showSecondaryButton = true,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const isXLargeScreen = useMediaQuery(theme.breakpoints.up("xl"));

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Handle scroll animations
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: { xs: "100vh", sm: "90vh", md: "95vh" },
          maxHeight: { xs: "none", md: "1000px" },
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: { xs: "center", md: "center top" },
          overflow: "hidden",
          mb: { xs: 0, sm: 2, md: 3 },
          borderRadius: { xs: 0, sm: "15px", md: "20px" },
          display: "flex",
          alignItems: "center",
          transition: "all 0.3s ease-in-out",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              linear-gradient(
                135deg, 
                rgba(0,0,0,0.3) 0%,
                rgba(0,0,0,0.4) 100%
              )
            `,
            zIndex: 1
          }
        }}
        id="hero-section"
      >
        {/* Overlay gradient - enhanced for better content visibility */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.6))",
            zIndex: 2,
          }}
        />

        <Container
          maxWidth="xl"
          sx={{
            position: "relative",
            py: { xs: 4, sm: 6, md: 8 },
            px: { xs: 2, sm: 3, md: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            zIndex: 3,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              alignItems: "center",
              justifyContent: { xs: "center", lg: "space-between" },
              gap: { xs: 0, lg: 4 },
              mt: { xs: 2, sm: 4, md: 6 },
            }}
          >
            {/* Text content container with enhanced animations */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", lg: "flex-start" },
                justifyContent: "center",
                textAlign: { xs: "center", lg: "left" },
                maxWidth: { xs: "100%", lg: "45%" },
                mb: { xs: 4, lg: 0 },
                opacity: 1,
                transform: "translateY(0)",
                animation: "fadeInUp 0.8s ease-out",
                "@keyframes fadeInUp": {
                  "0%": {
                    opacity: 0,
                    transform: "translateY(20px)"
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translateY(0)"
                  }
                }
              }}
            >
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", sm: "3rem", md: "3.5rem", lg: "4rem" },
                  color: "white",
                  mb: { xs: 2, sm: 3 },
                  lineHeight: { xs: 1.2, sm: 1.25 },
                  maxWidth: "100%",
                  textShadow: "0px 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                {title}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" },
                  color: "#E5E7EB",
                  maxWidth: { xs: "100%", sm: "85%", md: "75%" },
                  mb: { xs: 3, sm: 4, md: 5 },
                  opacity: 0.9,
                  textShadow: "0px 1px 2px rgba(0,0,0,0.3)",
                }}
              >
                {subtitle}
              </Typography>

              <Stack
                sx={{
                  width: "100%",
                  justifyContent: { xs: "center", lg: "flex-start" },
                }}
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 2, sm: 3 }}
                alignItems="center"
              >
                <ActionButton
                  variant="contained"
                  color="primary"
                  size={isMobile ? "medium" : "large"}
                  sx={{
                    fontSize: { xs: '15px', sm: '16px', md: '18px' },
                    bgcolor: "#000000",
                    "&:hover": {
                      bgcolor: "#333333",
                      transform: "translateY(-3px)",
                      boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.4)",
                    },
                    minWidth: { xs: "160px", sm: "180px", md: "200px" },
                    height: { xs: "46px", sm: "50px", md: "54px" },
                    animation: "pulseButton 2s infinite",
                    "@keyframes pulseButton": {
                      "0%": {
                        boxShadow: "0 0 0 0 rgba(0, 0, 0, 0.5)"
                      },
                      "70%": {
                        boxShadow: "0 0 0 10px rgba(0, 0, 0, 0)"
                      },
                      "100%": {
                        boxShadow: "0 0 0 0 rgba(0, 0, 0, 0)"
                      }
                    }
                  }}
                  onClick={() => window.location.href = ctaButtonLink}
                >
                  {ctaButtonText}
                </ActionButton>

                {showSecondaryButton && (
                  <Button
                    variant="text"
                    onClick={handleOpenModal}
                    sx={{
                      color: "white",
                      textDecoration: "none",
                      textTransform: "none",
                      fontSize: { xs: '15px', sm: '16px', md: '18px' },
                      fontWeight: 500,
                      position: "relative",
                      "&:hover": {
                        color: "#f0f0f0",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                      py: { xs: 1.2, sm: 1.5 },
                      px: { xs: 2, sm: 3 },
                      borderRadius: "8px",
                      transition: "all 0.3s ease",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        width: "0%",
                        height: "2px",
                        bottom: "6px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "white",
                        transition: "width 0.3s ease"
                      },
                      "&:hover::after": {
                        width: "80%"
                      }
                    }}
                  >
                    {secondaryButtonText}
                  </Button>
                )}
              </Stack>
            </Box>

            {/* Image container with enhanced animations */}
            <Box
              sx={{
                width: { xs: "100%", sm: "90%", md: "85%", lg: "50%" },
                mt: { xs: 2, sm: 4, lg: 0 },
                px: { xs: 1, sm: 2, md: 0 },
                position: "relative",
                display: "flex",
                justifyContent: "center",
                transform: { lg: "translateY(0)" },
                animation: "fadeInRight 0.8s ease-out 0.2s both",
                "@keyframes fadeInRight": {
                  "0%": {
                    opacity: 0,
                    transform: "translateX(20px)"
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translateX(0)"
                  }
                },
                transition: "transform 0.5s ease-in-out",
                "&:hover": {
                  transform: { md: "translateY(-8px)" },
                }
              }}
            >
              {/* Image platform shadow effect */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: "-4%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "90%",
                  height: "20px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(0,0,0,0.35)",
                  filter: "blur(10px)",
                  zIndex: 2,
                  display: { xs: "none", md: "block" }
                }}
              />

              <Box
                component="img"
                src={imageUrl}
                alt={imageAlt}
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: "95%", md: "100%" },
                  height: "auto",
                  borderRadius: { xs: "10px", sm: "12px", md: "14px" },
                  boxShadow: "0px 15px 35px rgba(0, 0, 0, 0.3)",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  transition: "all 0.4s ease-in-out",
                  zIndex: 3,
                  filter: "drop-shadow(0 10px 15px rgba(0,0,0,0.2))",
                  "&:hover": {
                    boxShadow: { md: "0px 20px 40px rgba(0, 0, 0, 0.4)" },
                    transform: { md: "scale(1.01)" }
                  },
                }}
              />

              {/* Optional decorative elements for larger screens */}
              {isLargeScreen && (
                <>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "5%",
                      right: "-5%",
                      width: "150px",
                      height: "150px",
                      borderRadius: "50%",
                      background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)",
                      zIndex: 1,
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "15%",
                      left: "-8%",
                      width: "100px",
                      height: "100px",
                      borderRadius: "50%",
                      background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 70%)",
                      zIndex: 1,
                    }}
                  />
                </>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {showSecondaryButton && <CalendarModal open={modalOpen} handleClose={handleCloseModal} />}

      {/* Global styles */}
      <style jsx global>{`
        /* Improved responsive styles */
        @media (max-width: 600px) {
          .hide-on-mobile {
            display: none !important;
          }
        }
        
        /* Smooth scrolling for the whole page */
        html {
          scroll-behavior: smooth;
        }
        
        /* Better handling of mobile viewports */
        @media (max-width: 768px) {
          body {
            -webkit-text-size-adjust: 100%;
          }
        }
        
        /* Prevent content shifting on different device widths */
        * {
          box-sizing: border-box;
        }
        
        /* Better handling of image loading */
        img {
          opacity: 1;
          transition: opacity 0.3s;
        }
        
        img.loading {
          opacity: 0;
        }
        
        /* Improved text readability on dark backgrounds */
        @media (max-width: 600px) {
          h1, h2, h3, h4, h5, h6, p {
            text-shadow: 0 1px 3px rgba(0,0,0,0.3);
          }
        }
        
        /* Reduce motion for users who prefer it */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
      `}</style>
    </>
  );
};

export default HeaderLayout;